import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'

import {LABELS} from '../constants/labels'
import type {IssueFieldChangedEventNode} from './issue-viewer-page-query-types'
import type {IssueFieldAddedEventFragment} from './IssueFieldAddedEvent'
import {IssueFieldEvent, type IssueFieldEventBaseProps, normalizeMultiSelectOption} from './IssueFieldEvent'
import type {IssueFieldRemovedEventFragment} from './IssueFieldRemovedEvent'
import {RolledupIssueFieldEvent} from './RolledupIssueFieldEvent'

type IssueFieldEventKey = IssueFieldAddedEventFragment | IssueFieldChangedEventFragment | IssueFieldRemovedEventFragment

// RELAY-CLEANUP(IssueFieldChangedEvent): widens nullable fields so still-Relay parent data is assignable.
// Remove when the timeline pagination fragments leave Relay.
export type IssueFieldChangedEventFragment = RelayCompat<
  Pick<
    IssueFieldChangedEventNode,
    'actor' | 'createdAt' | 'databaseId' | 'intent' | 'issueField' | 'newColor' | 'newOptions' | 'newValue'
  >
>

type IssueFieldChangedEventProps = IssueFieldEventBaseProps & {
  queryRef: IssueFieldChangedEventFragment
  rollupGroup?: Record<string, IssueFieldEventKey[]>
}

export function IssueFieldChangedEvent({queryRef, rollupGroup, ...props}: IssueFieldChangedEventProps) {
  const {actor, createdAt, issueField, newValue, newColor, newOptions, databaseId, intent} = queryRef
  const fieldOptions = newOptions?.map(option => normalizeMultiSelectOption(option.name, option.color))

  // If there's a rollup group, render the bundled view
  const rollupContent = rollupGroup ? (
    <RolledupIssueFieldEvent rollupGroup={rollupGroup} repositoryNameWithOwner={props.repositoryNameWithOwner} />
  ) : undefined

  return (
    <IssueFieldEvent
      actor={actor}
      createdAt={createdAt}
      fieldName={issueField?.name}
      dataType={issueField?.dataType}
      fieldValue={newValue}
      fieldOptions={fieldOptions}
      color={newColor}
      databaseId={databaseId}
      leadingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldChanged.leading}
      trailingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldChanged.trailing}
      hasValue={!rollupGroup}
      rollupContent={rollupContent}
      rationale={rollupGroup ? undefined : (intent?.rationale ?? undefined)}
      analyticsAction="ISSUE_FIELD_CHANGED"
      {...props}
    />
  )
}
